import type { ReactNode } from "react";
import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { useLocation, useMatch } from "react-router-dom";

interface ICurrentPageContext {
  pageId: string | undefined;
  setPageId: (pageId: string | undefined) => void;
  isHomePage: boolean;
  isNavLoaded: boolean;
  setIsNavLoaded: (isLoaded: boolean) => void;
  isMobileNavOpen: boolean;
  setIsMobileNavOpen: (isOpen: boolean) => void;
}

const CurrentPageContext = createContext<ICurrentPageContext | undefined>(undefined);

interface ICurrentPageProviderProps {
  children: ReactNode;
}

/**
 * Provider component for current page context
 * Wraps the application to provide page ID state across components
 */
export function CurrentPageProvider({ children }: ICurrentPageProviderProps): ReactNode {
  const [pageId, setPageIdState] = useState<string | undefined>(undefined);
  const [isNavLoaded, setIsNavLoadedState] = useState<boolean>(false);
  const [mobileNavState, setMobileNavState] = useState<{ isOpen: boolean; path: string }>({
    isOpen: false,
    path: "",
  });
  const location = useLocation();

  // Check if we're on the home page
  // Valid locale patterns: 'en-US', 'fr-FR', 'zh-Hant', 'zh-Hans', etc.
  const isRootMatch = useMatch("/");
  const isChangeLocale = location.pathname === "/change-locale";
  
  // Check if path is a valid locale home page (e.g., /en-US, /zh-Hant)
  const localePattern = /^\/[a-z]{2}-[A-Z][a-zA-Z]{1,3}$/;
  const isLocaleHome = localePattern.test(location.pathname);
  
  const isHomePage = !isChangeLocale && (Boolean(isRootMatch) || isLocaleHome);

  // Determine if we're on a content page (excludes home and locale picker)
  const isContentPage =
    !isHomePage &&
    !isChangeLocale;

  const setPageId = useCallback((id: string | undefined) => {
    setPageIdState(id);
  }, []);

  const setIsNavLoaded = useCallback((isLoaded: boolean) => {
    setIsNavLoadedState(isLoaded);
  }, []);

  const setIsMobileNavOpen = useCallback((isOpen: boolean) => {
    setMobileNavState({ isOpen, path: location.pathname });
  }, [location.pathname]);

  // Auto-close mobile nav on route change by scoping open-state to the current route.
  const isMobileNavOpen = mobileNavState.isOpen && mobileNavState.path === location.pathname;

  const value = useMemo(
    () => ({
      // Only expose pageId if we're on a content page
      pageId: isContentPage ? pageId : undefined,
      setPageId,
      isHomePage,
      isNavLoaded,
      setIsNavLoaded,
      isMobileNavOpen,
      setIsMobileNavOpen,
    }),
    [pageId, setPageId, isContentPage, isHomePage, isNavLoaded, setIsNavLoaded, isMobileNavOpen, setIsMobileNavOpen]
  );

  return <CurrentPageContext.Provider value={value}>{children}</CurrentPageContext.Provider>;
}

/**
 * Hook to access the current page context
 * Use this to get or set the current page ID from any component
 */
export function useCurrentPage(): ICurrentPageContext {
  const context = useContext(CurrentPageContext);
  if (context === undefined) {
    throw new Error("useCurrentPage must be used within a CurrentPageProvider");
  }
  return context;
}
